/**
 * Project Kanban View
 *
 * Uses the global Kanban component with renovation-specific configuration.
 * All column definitions come from renovationPhaseConfig.ts
 */

import { useState, useEffect, useRef } from "react";
import { Warning } from "@phosphor-icons/react";
import { Tooltip } from "@heroui/react";
import ProjectCard from "./projectCard";
import { ProjectWarning } from "../../utils/projectOrganizer";
import { useNavigate } from "react-router-dom";
import { ProjectStatus, RenovationProject } from "shared/types/renovation";
import { Kanban } from "shared/components/Kanban";
import { renovationKanbanConfig } from "shared/components/Kanban/configs/renovationKanbanConfig";
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { toast } from "react-toastify";
import { getProjectYear } from "../../utils/projectOrganizer";
import { useSettings } from "shared/contexts/settings.context";

interface ProjectKanbanViewProps {
  projects: RenovationProject[];
  warnings: ProjectWarning[];
  buildingId: string;
  onUpdate: () => void;
  highlightProjectId?: number | null;
}

export const ProjectKanbanView = ({
  projects,
  warnings,
  buildingId,
  onUpdate,
  highlightProjectId,
}: ProjectKanbanViewProps) => {
  const { session } = useSession();
  const navigate = useNavigate();
  const highlightedProjectRef = useRef<HTMLDivElement>(null);
  const { getRenovationCardSettings } = useSettings();

  // Local state for optimistic updates
  const [localProjects, setLocalProjects] = useState(projects);

  // Get settings
  const settings = getRenovationCardSettings();

  // Track collapsed state for each card (key: projectId, value: isCollapsed)
  const [collapsedCards, setCollapsedCards] = useState<Record<number, boolean>>(
    {},
  );

  // Track per-card "Projectonderdelen" expand state (key: projectId, value: isExpanded)
  const [expandedPartsCards, setExpandedPartsCards] = useState<
    Record<number, boolean>
  >({});

  // When defaultCollapsed setting changes, apply it to ALL cards immediately
  useEffect(() => {
    const updatedCollapsedState: Record<number, boolean> = {};
    projects.forEach((project) => {
      updatedCollapsedState[project.id] = settings.defaultCollapsed;
    });
    setCollapsedCards(updatedCollapsedState);
  }, [settings.defaultCollapsed]);

  // When partsExpanded setting changes, apply it to ALL cards immediately
  useEffect(() => {
    const updated: Record<number, boolean> = {};
    projects.forEach((project) => {
      updated[project.id] = settings.partsExpanded;
    });
    setExpandedPartsCards(updated);
  }, [settings.partsExpanded]);

  // Initialize new projects with the default collapsed state
  useEffect(() => {
    setCollapsedCards((prev) => {
      const newState = { ...prev };
      projects.forEach((project) => {
        if (newState[project.id] === undefined) {
          newState[project.id] = settings.defaultCollapsed;
        }
      });
      return newState;
    });
    setExpandedPartsCards((prev) => {
      const newState = { ...prev };
      projects.forEach((project) => {
        if (newState[project.id] === undefined) {
          newState[project.id] = settings.partsExpanded;
        }
      });
      return newState;
    });
  }, [projects]);

  // Update local state when prop changes
  useEffect(() => {
    setLocalProjects(projects);
  }, [projects]);

  // Toggle collapse for a specific card
  const handleToggleCollapse = (projectId: number) => {
    setCollapsedCards((prev) => ({
      ...prev,
      [projectId]: !prev[projectId],
    }));
  };

  // Toggle "Projectonderdelen" expand for a specific card
  const handleTogglePartsExpanded = (projectId: number) => {
    setExpandedPartsCards((prev) => ({
      ...prev,
      [projectId]: !prev[projectId],
    }));
  };

  // Scroll to highlighted project when it becomes visible
  useEffect(() => {
    if (highlightProjectId) {
      const scrollToProject = () => {
        if (highlightedProjectRef.current) {
          console.log("Scrolling to highlighted project:", highlightProjectId);
          highlightedProjectRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          return true;
        }
        return false;
      };

      // Try to scroll immediately
      if (scrollToProject()) return;

      // If element not ready, try again with increasing delays
      const attempts = [100, 300, 600, 1000, 1500];
      const timers: NodeJS.Timeout[] = [];

      attempts.forEach((delay, index) => {
        const timer = setTimeout(() => {
          if (scrollToProject()) {
            // Clear remaining timers if successful
            timers.slice(index + 1).forEach(clearTimeout);
          }
        }, delay);
        timers.push(timer);
      });

      return () => timers.forEach(clearTimeout);
    }
  }, [highlightProjectId]);

  // Budget now comes from investment item part assignments
  const getTotalBudgetAmount = (project: any): string => {
    const total = project.parts?.reduce(
      (sum: number, part: any) =>
        sum + (part.investmentAssignments?.reduce(
          (partSum: number, assignment: any) => partSum + Number(assignment.assignedAmount),
          0,
        ) || 0),
      0,
    ) || 0;
    return `€${total.toFixed(2)}`;
  };

  const getTotalProposalAmount = (project: any): string => {
    const total =
      project.parts?.reduce(
        (sum: number, part: any) =>
          sum +
            part.documentLines
              ?.filter(
                (line: any) =>
                  line.document.type === "OFFER" &&
                  line.document.status === "APPROVED",
              )
              .reduce(
                (partSum: number, line: any) =>
                  partSum + Number(line.totalInclVat),
                0,
              ) || 0,
        0,
      ) || 0;
    return `€${total.toFixed(2)}`;
  };

  const getTotalInvoiceAmount = (project: any): string => {
    const total =
      project.parts?.reduce((sum: number, part: any) => {
        const partTotal =
          part.documentLines
            ?.filter((line: any) => line.document.type === "INVOICE")
            ?.reduce(
              (partSum: number, line: any) =>
                partSum + Number(line.totalInclVat),
              0,
            ) || 0;
        return sum + partTotal;
      }, 0) || 0;
    return `€${total.toFixed(2)}`;
  };

  /**
   * Handle drop from the Kanban component
   */
  const handleDrop = async (
    projectId: string | number,
    newStatus: ProjectStatus,
    oldStatus: ProjectStatus,
  ) => {
    // OPTIMISTIC UPDATE: Update local state immediately
    setLocalProjects((prev) =>
      prev.map((p) => (p.id === projectId ? { ...p, status: newStatus } : p)),
    );

    // Make API call in background
    try {
      const response = await fetchWithAuth(
        `${API_URL}/renovation/project/${projectId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: newStatus }),
        },
        session,
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to update project status");
      }

      toast.success(`Project verplaatst naar "${newStatus}"`);
    } catch (error: any) {
      console.error("Error updating project status:", error);
      toast.error(
        `Fout bij verplaatsen project: ${error.message || "Onbekende fout"}`,
      );

      // ROLLBACK: Revert local state on error
      setLocalProjects((prev) =>
        prev.map((p) => (p.id === projectId ? { ...p, status: oldStatus } : p)),
      );

      // Refetch to ensure data consistency
      onUpdate();
    }
  };

  // Handle status change from dropdown (same optimistic update as drag-and-drop)
  const handleStatusChange = (projectId: number, newStatus: ProjectStatus) => {
    setLocalProjects((prev) =>
      prev.map((p) => (p.id === projectId ? { ...p, status: newStatus } : p)),
    );
  };

  /**
   * Render a project card with warnings and highlighting
   */
  const renderProjectCard = (project: RenovationProject) => {
    const projectWarnings = warnings.filter((w) => w.projectId === project.id);
    const projectYear = getProjectYear(project);

    return (
      <div className="relative">
        {projectWarnings.length > 0 && (
          <div className="absolute -top-2 -right-2 z-10">
            <Tooltip
              content={
                <div className="max-w-sm space-y-2">
                  <div className="mb-2 flex items-center gap-2">
                    <div className="text-brick-primary text-sm font-semibold">
                      Waarschuwingen voor {project.title}
                    </div>
                    <div className="bg-brick-primary/10 text-brick-primary rounded-sm px-2 py-1 font-mono text-xs">
                      RENO-{project.id}
                    </div>
                  </div>
                  {projectWarnings.map((warning, idx) => (
                    <div
                      key={idx}
                      className="bg-brick-primary/5 border-brick-primary/30 rounded-sm border-l-2 p-2 text-xs"
                    >
                      <div className="text-neutral-700">{warning.message}</div>
                    </div>
                  ))}
                </div>
              }
              placement="left"
              className="cursor-help"
            >
              <div className="from-brick-primary to-brick-primary/80 flex h-6 w-6 cursor-help items-center justify-center rounded-full bg-linear-to-br shadow-md ring-2 ring-white transition-all duration-200 hover:scale-110 hover:shadow-lg">
                <Warning size={12} className="text-white" weight="bold" />
              </div>
            </Tooltip>
          </div>
        )}

        <ProjectCard
          id={project.id}
          buildingId={buildingId}
          status={project.status}
          description={project.description}
          units={project.units || []}
          title={project.title}
          responsibleUserId={project.responsibleUserId}
          budgetAmount={getTotalBudgetAmount(project)}
          proposalAmount={getTotalProposalAmount(project)}
          invoiceAmount={getTotalInvoiceAmount(project)}
          projectDates={project.dates || []}
          projectYear={projectYear}
          actionOnClick={() =>
            navigate(`/renovation/${project.id}?buildingId=${buildingId}`)
          }
          parts={project.parts || []}
          onUpdate={onUpdate}
          isHighlighted={highlightProjectId === project.id}
          onStatusChange={handleStatusChange}
          isCollapsed={collapsedCards[project.id] ?? settings.defaultCollapsed}
          onToggleCollapse={() => handleToggleCollapse(project.id)}
          isPartsExpanded={
            expandedPartsCards[project.id] ?? settings.partsExpanded
          }
          onTogglePartsExpanded={() => handleTogglePartsExpanded(project.id)}
          visibleSections={settings.visibleSections}
        />
      </div>
    );
  };

  return (
    <Kanban
      config={renovationKanbanConfig}
      items={localProjects}
      renderCard={renderProjectCard}
      onDrop={handleDrop}
      onCardClick={(project) =>
        navigate(`/renovation/${project.id}?buildingId=${buildingId}`)
      }
    />
  );
};
